// TODO: This is mostly internal utils / helpers
// a lot is experimenting with APIs and ergonomics
// These are not the public utils that we plan to export.

Observable from @danielx/observable

export A := <T extends {}>(attr: keyof T) ->
  (x: T) -> x[attr]

export F := <T extends Record<keyof T, () => any>>(methodName: keyof T) ->
  (x: T) -> x[methodName]() as ReturnType<T[keyof T]>

type ThisParameterType<T> = T extends (this: infer U, ...args: any[]) => any ? U : unknown;

export type Fn = (...args: any) => any

export get := <T extends () => any>(x: T, context: ThisParameterType<T>) ->
  if typeof x is 'function'
    x.call(context)
  else
    x

// Observable attribute helper
export o := <T extends {}, K extends keyof T>(object: T, name: K) =>
  attribute := Observable(object[name])

  attribute.observe (newValue) =>
    //@ts-expect-error
    object[name] = newValue

  return attribute

// Handle events by preventing the default action
export handle := <T extends Event, F extends Fn>(fn: F) =>
  (this: ThisParameterType<F>, e: T) ->
    return if e?.defaultPrevented
    e?.preventDefault()
    fn.call(this, e)

// I hope I don't hate myself for this later
// S for Safe invoke, invoke the method of the object, if it exists and is a
// function, otherwise return the provided default value
export S := <T extends Record<PropertyKey, Fn>, K extends keyof T>(object: T, method: K, defaultValue: T[K]) =>
  ->
    if typeof object?[method] is 'function'
      object[method]() as ReturnType<T[K]>
    else
      defaultValue

export asElement := A('element')

export accelerateItem := <T extends {accelerator: string, click: Fn}[]>(items: T, key: string) ->
  [acceleratedItem] := items.filter (item) ->
    item.accelerator is key

  if acceleratedItem
    // TODO: should there be some kind of exec rather than click action?
    acceleratedItem.click()

export isDescendant := (element: Element, ancestor: Element) ->
  return unless element

  while (parent := element.parentElement)
    return true if element is ancestor
    element = parent

export advance := <T extends {active: () => boolean}[]>(list: T, amount: number) ->
  [currentItem] := list.filter (item) ->
    item.active()

  activeItemIndex .= list.indexOf(currentItem) + amount

  if activeItemIndex < 0
    activeItemIndex = list.length - 1
  else if activeItemIndex >= list.length
    activeItemIndex = 0

  list[activeItemIndex]

// TODO: Nested objects?
// TODO: Convert keys ending in [] to array entries?
// Just keeping it simple and crushing duplicate names
export formDataToObject := (formData: FormData) ->
  Array.from(formData.entries()).reduce (object, [key, value]) ->
    object[key] = value

    object
  , {} as Record<string, FormDataEntryValue>

// Get the view associated with a dom element
// This will let us use the dom tree rather than manage a separate tree
// to dispatch events at the view level
// the assumption is that a .view property is written to the root element in the
// view when rendering a view's template element
export elementView := (element: HTMLElement? | null): unknown ->
  return unless element
  //@ts-expect-error
  return element.view if element.view
  elementView element.parentElement

// Remove all children from a dom node
export empty := (node: Node) ->
  while node.hasChildNodes()
    node.removeChild(node.lastChild!)

  return node

export htmlEscape := (string: string) ->
  String(string).replace /[&<>"'\/]/g, (s) ->
    entityMap[s as keyof typeof entityMap]

entityMap :=
  "&": "&amp;"
  "<": "&lt;"
  ">": "&gt;"
  '"': '&quot;'
  "'": '&#39;'
  "/": '&#x2F;'

// Load scripts sequentially, prevents failures if there is a dependency order
export loadScripts := (urls: string[]) =>
  urls.reduce (p, url) =>
    await p

    // Resolve if present
    if document.querySelector `script[src=${JSON.stringify(url)}]`
      return Promise.resolve()

    script := document.createElement "script"
    script.src = url
    document.body.appendChild script

    return new Promise<Event> (resolve, reject) =>
      script.onload = resolve
      script.onerror = reject
  , Promise.resolve<Event | void>(undefined)

// Takes an array of urls, returns a decorator that checks the deps have resolved
// before invoking the given function
export lazyLoader := (urls: string[]) ->
  // Load the dependencies keeping a promise to limit to only one request
  // clearing the limit on failure
  // caching on success
  let loadingDeps: Promise<Event | void> | null
  _load := ->
    if loadingDeps
      return loadingDeps

    try
      loadingDeps = loadScripts(urls)
    finally
      loadingDeps = null

  // Decorator to ensure initialized
  return <T extends Fn>(fn: T) ->
    (this: ThisParameterType<T>, ...args: Parameters<T>) ->
      context := this
      await _load()

      fn.apply context, args
